import { CheckCircle2, XCircle } from "lucide-react";
import { fmtNum } from "@/lib/testFormat";

export function TurnsTable({ turnAnalysis }) {
  if (!turnAnalysis) return null;
  const { turns = [], expectedSequence = [], detectedSequence = [], sequenceOk, method, threshold } = turnAnalysis;
  const rateUnit = method === "accelX" ? "g" : "°/s";

  return (
    <section data-testid="turns-table" className="bg-zinc-900 border border-white/10 rounded-xl p-5 mb-8">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="text-sm font-heading font-semibold text-white">Detected Turns</h2>
          <p className="text-xs text-zinc-500 mt-1">
            Method: <span className="font-mono text-zinc-300">{method}</span> · threshold ±{threshold} {rateUnit} (smoothed)
          </p>
        </div>
        <div className="flex flex-col items-end gap-1.5 text-xs font-mono">
          <SequenceRow label="Expected" seq={expectedSequence} />
          <SequenceRow label="Detected" seq={detectedSequence} compare={expectedSequence} />
          <span
            data-testid="turn-sequence-status"
            className={`mt-1 px-2 py-0.5 rounded ${sequenceOk ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"}`}
          >
            {sequenceOk ? "Sequence correct" : "Sequence incorrect"}
          </span>
        </div>
      </div>

      {!turns.length ? (
        <p className="text-sm text-zinc-400">No turns detected between start and stop.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] font-mono uppercase text-zinc-500 text-left">
                <th className="py-2 pr-4 font-normal">#</th>
                <th className="py-2 pr-4 font-normal">Direction</th>
                <th className="py-2 pr-4 font-normal">Expected</th>
                <th className="py-2 pr-4 font-normal">Time</th>
                <th className="py-2 pr-4 font-normal">Duration</th>
                <th className="py-2 pr-4 font-normal">Peak rate</th>
                <th className="py-2 pr-4 font-normal">Angle</th>
                <th className="py-2 font-normal">Status</th>
              </tr>
            </thead>
            <tbody>
              {turns.map((t) => (
                <tr key={t.order} data-testid={`turn-row-${t.order}`} className="border-t border-white/5">
                  <td className="py-2 pr-4 font-mono text-zinc-400">{t.order}</td>
                  <td className="py-2 pr-4 text-white font-medium">{t.direction}</td>
                  <td className="py-2 pr-4 text-zinc-400">{t.expected || "—"}</td>
                  <td className="py-2 pr-4 font-mono text-zinc-300">{fmtNum(t.startT, 1)}–{fmtNum(t.endT, 1)} s</td>
                  <td className="py-2 pr-4 font-mono text-zinc-300">{t.durationMs} ms</td>
                  <td className="py-2 pr-4 font-mono text-zinc-300">{fmtNum(t.peakRate, 1)} {rateUnit}</td>
                  <td className="py-2 pr-4 font-mono text-zinc-300">{t.angleDeg == null ? "—" : `${t.angleDeg}°`}</td>
                  <td className="py-2">
                    {t.ok ? (
                      <span className="inline-flex items-center gap-1 text-emerald-400 text-xs"><CheckCircle2 className="w-3.5 h-3.5" /> OK</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-red-400 text-xs"><XCircle className="w-3.5 h-3.5" /> {(t.issues || []).join(", ")}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function SequenceRow({ label, seq, compare }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-zinc-500 w-16 text-right">{label}</span>
      {seq.length ? (
        seq.map((d, i) => {
          const bad = compare && compare[i] !== d;
          return (
            <span
              key={`${label}-${i}`}
              className={`w-6 h-6 rounded flex items-center justify-center text-[11px] font-bold ${
                bad ? "bg-red-500/20 text-red-300" : "bg-zinc-800 text-zinc-200"
              }`}
            >
              {d[0]}
            </span>
          );
        })
      ) : (
        <span className="text-zinc-600">none</span>
      )}
    </div>
  );
}
